/**
 * Fire-and-forget TTS generation for newly created/updated cards.
 * Never blocks the UI. Runs in background after deck/card save.
 * Uses resolveEffectiveSpeechLocale for correct language (incl. uk/ru conflict override).
 */
import { resolveEffectiveSpeechLocale } from "./cardLanguageUtils";
import { sanitizeTextForSpeech } from "./speechText";

function getApiBase(): string {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

/**
 * Trigger TTS generation for cards that lack audio_url.
 * Fire-and-forget: does not await. Safe to call after createDeck/updateDeckInDB.
 * Staggers requests slightly to avoid hammering the API.
 */
export function triggerBackgroundTtsForCards(
  cards: Array<{ id?: string; front?: string; back?: string; frontLang?: string; backLang?: string; frontAudioUrl?: string | null; backAudioUrl?: string | null }>,
  deckLang: string
): void {
  if (typeof window === "undefined") return;
  const base = getApiBase();
  if (!base) return;

  const tasks: { cardId: string; text: string; locale: string; side: "front" | "back" }[] = [];
  for (const card of cards) {
    const id = card.id;
    if (!id) continue;
    const frontText = sanitizeTextForSpeech(card.front ?? "");
    const backText = sanitizeTextForSpeech(card.back ?? "");
    if (frontText && !card.frontAudioUrl) {
      tasks.push({
        cardId: id,
        text: frontText,
        locale: resolveEffectiveSpeechLocale({ card, side: "front", deckLang, fallback: "en-US" }),
        side: "front",
      });
    }
    if (backText && !card.backAudioUrl) {
      tasks.push({
        cardId: id,
        text: backText,
        locale: resolveEffectiveSpeechLocale({ card, side: "back", deckLang, fallback: "en-US" }),
        side: "back",
      });
    }
  }

  if (tasks.length === 0) return;

  const apiPath = "/api/generate-audio";
  tasks.forEach((task, i) => {
    const delay = i * 150;
    setTimeout(() => {
      fetch(`${base}${apiPath}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: task.text,
          locale: task.locale || "en-US",
          cardId: task.cardId,
          side: task.side,
        }),
      })
        .then(async (res) => {
          const data = await res.json();
          if (!res.ok) console.warn("[triggerBackgroundTts]", data?.error ?? "Failed");
        })
        .catch((err) => console.warn("[triggerBackgroundTts]", err));
    }, delay);
  });
}
